// utils/authApi.ts
// ─────────────────────────────────────────────────────────────────────────────
// Matches app/modules/auth/router.py exactly.
// Endpoints: /auth/register  /auth/login  /auth/magic-link  /auth/verify-otp
//            /auth/me  /auth/refresh  /auth/google  /auth/github  /auth/logout

import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from './firebase';


export const API = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

const TOKEN_KEY   = 'cloudairambo_jwt';
const REFRESH_KEY = 'cloudairambo_refresh';
const USER_KEY    = 'cloudairambo_user';

// ─── Types ────────────────────────────────────────────────────────────────────
export type PlanId = 'basic' | 'pro' | 'ultra';
export type PlanTier = 'individual' | 'smb' | 'enterprise';

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  picture: string | null;
  is_active: boolean;
  is_subscribed: boolean;
  plan: PlanId;
  plan_tier: PlanTier;
  plan_expires_at?: string | null;
  role: string;
  outlet_id?: number | null;
  outlet_name?: string | null;
  org_id: number | null;
  onboarding_completed: boolean;
  created_at: string;
  outlet_whatsapp_number?: string | null;
  permissions?: Record<string, boolean>;
  policy_accepted: boolean;
}

export interface AuthResult {
  user: AuthUser;
  access_token: string;
  refresh_token: string;
  is_new_user: boolean;
}

// ─── Storage helpers ──────────────────────────────────────────────────────────
export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function saveSession(access: string, refresh: string, user: AuthUser) {
  localStorage.setItem(TOKEN_KEY,   access);
  localStorage.setItem(REFRESH_KEY, refresh);
  localStorage.setItem(USER_KEY,    JSON.stringify(user));
}
export function clearSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

// ─── Internal fetch wrapper ───────────────────────────────────────────────────
async function post<T>(path: string, body: object, token?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Request failed');
  return data;
}

// ─── Auth API ─────────────────────────────────────────────────────────────────

/** Email + password register */
export async function signUp(name: string, email: string, password: string, accountType: string = 'individual', companyName: string = ''): Promise<AuthResult> {
  const data = await post<any>('/auth/register', { name, email, password, account_type: accountType, company_name: companyName || undefined });
  const user = await fetchMe(data.access_token);
  saveSession(data.access_token, data.refresh_token, user);
  return { ...data, user };
}

/** Email + password login */
export async function signIn(email: string, password: string): Promise<AuthResult> {
  const data = await post<any>('/auth/login', { email, password });
  const user = await fetchMe(data.access_token);
  saveSession(data.access_token, data.refresh_token, user);
  return { ...data, user };
}

/** Magic link — step 1: request OTP */
export async function requestMagicLink(email: string): Promise<void> {
  await post('/auth/magic-link', { email });
}

/** Magic link — step 2: verify OTP */
export async function verifyOTP(email: string, otp: string): Promise<AuthResult> {
  const data = await post<any>('/auth/verify-otp', { email, otp });
  const user = await fetchMe(data.access_token);
  saveSession(data.access_token, data.refresh_token, user);
  return { ...data, user };
}

/** Forgot password — step 1: request OTP */
export async function forgotPassword(email: string): Promise<void> {
  await post('/auth/forgot-password', { email });
}

/** Forgot password — step 2: verify OTP + set new password */
export async function resetPassword(email: string, otp: string, newPassword: string): Promise<void> {
  await post('/auth/reset-password', { email, otp, new_password: newPassword });
}

/** Get current user from /auth/me */
export async function fetchMe(token: string): Promise<AuthUser> {
  const res = await fetch(`${API}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Session expired');
  return res.json();
}

/** Refresh access token */
export async function refreshAccessToken(): Promise<string | null> {
  const refresh_token = getStoredRefreshToken();
  if (!refresh_token) return null;
  try {
    const data = await post<any>('/auth/refresh', { refresh_token });
    const user = await fetchMe(data.access_token);
    saveSession(data.access_token, data.refresh_token, user);
    return data.access_token;
  } catch {
    clearSession();
    return null;
  }
}

/** Fetch /auth/me with auto-refresh on 401 */
export async function fetchMeWithRefresh(): Promise<AuthUser | null> {
  let token = getStoredToken();
  if (!token) return null;
  try {
    return await fetchMe(token);
  } catch {
    token = await refreshAccessToken();
    if (!token) return null;
    try { return await fetchMe(token); }
    catch { return null; }
  }
}

/** Redirect to Google OAuth */


export async function loginWithGoogle(): Promise<AuthResult> {
  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);
  const idToken = await result.user.getIdToken();
  const data = await post<any>('/auth/firebase', { id_token: idToken });
  const user = await fetchMe(data.access_token);
  saveSession(data.access_token, data.refresh_token, user);
  return { ...data, user };
}

/** Redirect to GitHub OAuth */
export function loginWithGitHub() {
  window.location.href = `${API}/auth/github`;
}

/** Logout */
export async function logout(token: string): Promise<void> {
  try {
    await fetch(`${API}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } finally {
    clearSession();
  }
}

/** Parse tokens from OAuth callback URL (?access_token=...&refresh_token=...&is_new=...) */
export async function handleOAuthCallback(): Promise<AuthResult | null> {
  const params = new URLSearchParams(window.location.search);
  const access_token  = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  const is_new_user   = params.get('is_new') === 'true';

  if (!access_token || !refresh_token) return null;

  // Clean URL
  window.history.replaceState({}, '', window.location.pathname);

  const user = await fetchMe(access_token);
  saveSession(access_token, refresh_token, user);
  return { access_token, refresh_token, user, is_new_user };
}
/** One-way upgrade: individual → organization owner */
export async function upgradeToOrganization(companyName: string): Promise<AuthUser> {
  const token = getStoredToken();
  const res = await fetch(`${API}/auth/upgrade-to-org`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ company_name: companyName }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || 'Upgrade failed');

  const updatedUser = await fetchMe(token!);
  const refresh = getStoredRefreshToken()!;
  saveSession(token!, refresh, updatedUser);
  return updatedUser;
}
export async function postLog(action: string, details: string, outletId?: number | null) {
  const token = getStoredToken();
  if (!token) return;
  try {
    await fetch(`${API}/logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action, details, outlet_id: outletId ?? undefined }),
    });
  } catch {}
}

export async function fetchLogs(outletId?: number | null): Promise<{
  logs: any[];
  backup_warning: boolean;
  expiring_count: number;
}> {
  const token = getStoredToken();
  const qs = outletId ? `?outlet_id=${outletId}` : '';
  const res = await fetch(`${API}/logs${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch logs');
  return res.json();
}